import { useAuth } from "../auth/AuthProvider";
import type { CurrentUser } from "../api/types";

/**
 * The three experiences the product presents.
 *
 * Not three account types: Owner and Employee are the two principal logins, and
 * a manager is an employee with direct reports. The role on the login is the
 * server's coarse capability line, and this maps it to the workspace the person
 * should see. It shapes navigation and starting screens -- it is **not** a
 * security boundary, which lives on the server.
 */
export type Persona = "owner" | "manager" | "employee";

export function personaFor(user: CurrentUser | null): Persona {
  // The server decides the workspace from identity, account capability and the
  // persisted reporting relationship, and sends it on /me. The client only
  // renders it -- it is never the security boundary.
  const workspace = user?.workspace;
  if (workspace === "owner" || workspace === "manager" || workspace === "employee") {
    return workspace;
  }
  // Fallback for a session cached before the workspace field existed.
  const role = user?.role;
  if (role === "owner" || role === "admin") return "owner";
  if (role === "manager") return "manager";
  return "employee";
}

export function usePersona(): Persona {
  const { user } = useAuth();
  return personaFor(user);
}

/** The label for the persona's starting screen. One title for every role. */
export const PERSONA_DESK_LABEL: Record<Persona, string> = {
  owner: "My Desk",
  manager: "My Desk",
  employee: "My Desk",
};
